import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import StudentLayout from '../../components/StudentLayout';
import { 
  FolderOpen, Calendar, DollarSign, Clock, 
  CheckCircle, PlayCircle, Flag, AlertCircle,
  Eye, FileText
} from 'lucide-react';
import { format, formatDistance } from 'date-fns';

const StudentProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'in-progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'on-hold': 'bg-gray-100 text-gray-800 border-gray-200',
      'review': 'bg-purple-100 text-purple-800 border-purple-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'text-red-500',
      'high': 'text-orange-500',
      'medium': 'text-yellow-500',
      'low': 'text-green-500'
    };
    return colors[priority] || 'text-gray-500';
  };

  const calculateProgress = (project) => {
    if (!project?.tasks || project.tasks.length === 0) return 0;
    const completedTasks = project.tasks.filter(task => task.status === 'completed').length;
    return Math.round((completedTasks / project.tasks.length) * 100);
  };

  const getTotalPaid = (project) => {
    if (!project?.payments) return 0;
    return project.payments
      .filter(payment => payment.status === 'verified')
      .reduce((sum, payment) => sum + payment.amount, 0);
  };

  const filteredProjects = projects.filter(project => {
    if (filter === 'all') return true;
    if (filter === 'active') return ['pending', 'in-progress', 'review'].includes(project.status);
    if (filter === 'completed') return project.status === 'completed';
    return project.status === filter;
  });

  const getStatusIcon = (status) => {
    const icons = {
      'pending': <Clock className="w-4 h-4" />,
      'in-progress': <PlayCircle className="w-4 h-4" />,
      'completed': <CheckCircle className="w-4 h-4" />,
      'review': <Eye className="w-4 h-4" />,
      'on-hold': <AlertCircle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-800">My Projects</h1>
          <div className="text-sm text-gray-500">
            {projects.length} total project{projects.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex space-x-4 mb-6">
            {[
              { key: 'all', label: 'All Projects' },
              { key: 'active', label: 'Active' },
              { key: 'in-progress', label: 'In Progress' },
              { key: 'completed', label: 'Completed' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === tab.key
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-800">{project.title}</h3>
                      <div className={`flex items-center px-2 py-1 rounded-full ${getPriorityColor(project.priority)}`}>
                        <Flag className="w-3 h-3 mr-1" />
                        <span className="text-xs font-medium capitalize">{project.priority}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{project.description}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-lg border text-xs font-medium ${getStatusColor(project.status)}`}>
                    <div className="flex items-center">
                      {getStatusIcon(project.status)}
                      <span className="ml-1">{project.status}</span>
                    </div>
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm text-gray-500">{calculateProgress(project)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${calculateProgress(project)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Project Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="flex items-center text-blue-600 mb-1">
                      <FileText className="w-4 h-4 mr-1" />
                      <span className="text-xs font-medium">Tasks</span>
                    </div>
                    <p className="text-lg font-bold text-blue-800">{project.tasks?.length || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="flex items-center text-green-600 mb-1">
                      <DollarSign className="w-4 h-4 mr-1" />
                      <span className="text-xs font-medium">Paid</span>
                    </div>
                    <p className="text-lg font-bold text-green-800">
                      LKR {getTotalPaid(project).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Project Details */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>Due: {format(new Date(project.endDate), 'MMM dd, yyyy')}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{formatDistance(new Date(project.endDate), new Date(), { addSuffix: true })}</span>
                  </div>
                </div>

                {/* Action Button */}
                <Link
                  to={`/student/projects/${project._id}`}
                  className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors text-center block font-medium"
                >
                  View Project Details
                </Link>
              </motion.div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {filter === 'all' ? 'No projects assigned yet' : `No ${filter} projects`}
              </h3>
              <p className="text-gray-500">
                {filter === 'all' 
                  ? 'Your assigned projects will appear here when available.' 
                  : `You don't have any ${filter} projects at the moment.`
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </StudentLayout>
  );
};

export default StudentProjects;